﻿<!--
 © Copyright 2024 webzaytsev.ru.
 @author Nikita Zaytsev <nikita@webzaytsev.ru>
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AppLink from '@/components/AppLink.vue';
import IconSearch from '@/components/icons/IconSearch.vue';
import IconBurger from '@/components/icons/IconBurger.vue';
import dog from '@/assets/images/catalog-dog-2x.png';
import cat from '@/assets/images/catalog-cat-2x.png';
import catalog from '@/assets/images/catalog-img-2x.png';
import HeaderSearch from '@/components/HeaderSearch.vue';
import { useRoute } from 'vue-router';
import AppHeaderLogo from '@/components/images/AppHeaderLogo.vue';

const mediaQuery = '(max-width: 1024px)';
const isMobile = window.matchMedia(mediaQuery).matches;
const route = useRoute();

const isMobileMenuActive = ref<boolean>(false);
const scrollPrev = ref<number>(window.scrollY);
const isHeaderHide = ref<boolean>(false);
const isCatalogShow = ref<boolean>(false);
const isSearchBarShow = ref<boolean>(false);

watch(isHeaderHide, (newVal) => {
    if (newVal && isCatalogShow.value) displayCatalog();
    if (newVal && isSearchBarShow.value) displaySearchBar();
});

scrollHandler();
window.addEventListener('scroll', scrollHandler);

function setIsMobileMenuActive() {
    if (isCatalogShow.value && isMobileMenuActive.value) displayCatalog();
    isMobileMenuActive.value = !isMobileMenuActive.value;
}

function scrollHandler() {
    if (isMobile) return;
    let scrolled = window.scrollY;
    isHeaderHide.value = scrolled > 100 && scrolled > scrollPrev.value;
    scrollPrev.value = scrolled;
}

const headerClass = computed((): string => {
    if (isMobile) {
        if (isMobileMenuActive.value) return 'translate-x-0';

        return 'lg:translate-x-0 translate-x-[350px]';
    }

    return '';
});

function displayCatalog() {
    isCatalogShow.value = !isCatalogShow.value;
}

function displaySearchBar() {
    isSearchBarShow.value = !isSearchBarShow.value;
}

function hideSearchBarAndCatalog() {
    isSearchBarShow.value = false;
    isCatalogShow.value = false;
    isMobileMenuActive.value = false;
}

function clearPhoneNumber(number: string): string {
    return number.replace(/\D/g, '');
}

function hideSearchBarAndCatalogByOutsideClick(e: Event) {
    if (isMobile) return;

    const target = e.target as HTMLElement;
    const isTargetBelongsHeader = target.closest('header.app-header');

    if (!isTargetBelongsHeader) {
        hideSearchBarAndCatalog();
    }
}

watch(route, hideSearchBarAndCatalog);

document.addEventListener('click', hideSearchBarAndCatalogByOutsideClick);
</script>

<template>
    <IconBurger :is-active="isMobileMenuActive" @click="setIsMobileMenuActive" v-if="isMobile" />
    <div
        class="fixed z-30 top-0 left-0 bottom-0 right-0 transition-all duration-300"
        :class="isMobileMenuActive ? 'bg-black/50' : 'bg-black/0'"
        v-if="isMobile && isMobileMenuActive"
        @click="setIsMobileMenuActive"
    />
    <RouterLink to="/" class="lg:hidden block ml-6 mt-4">
        <AppHeaderLogo class="h-auto w-[168px]" />
    </RouterLink>
    <header
        class="lg:pl-5 lg:pr-5 pl-12 fixed top-0 lg:left-0 left-auto lg:right-auto right-0 lg:w-full w-[295px] lg:bottom-auto bottom-0 z-40 bg-white transition-all duration-300 app-header"
        :class="`${headerClass} ${!isMobile && isHeaderHide ? '-translate-y-20' : 'translate-y-0'}`"
    >
        <div
            class="lg:max-w-container max-w-[156px] lg:mx-auto flex lg:flex-row flex-col lg:h-auto h-full w-full lg:justify-between justify-center lg:items-center xl:gap-x-9.5 gap-x-5 gap-y-11.5 xl:pr-7 bg-white relative z-20 lg:py-4.5 py-8"
        >
            <RouterLink
                to="/"
                class="group grow-0 shrink-0"
                :class="{
                    'opacity-0': scrollPrev < 450 && $route.name === 'home',
                }"
            >
                <AppHeaderLogo class="h-auto xl:w-[120px] lg:w-25 w-[134px] block" />
            </RouterLink>
            <div
                class="flex lg:flex-row flex-col lg:justify-end justify-start lg:items-center hg:gap-x-19 2xl:gap-x-10 xl:gap-x-8 gap-5 grow"
            >
                <button
                    class="border flex justify-center items-center xl:py-1.5 lg:py-1 py-1.5 xl:px-7.5 px-4 xl:gap-x-5 lg:gap-x-2 gap-x-5 rounded-60 cursor-pointer shrink-0 grow-0 transition-all duration-300"
                    :class="{
                        'border-turquoise bg-turquoise': !isCatalogShow,
                        'border-blue bg-white': isCatalogShow,
                    }"
                    @click="displayCatalog"
                >
                    <span class="">Каталог</span>
                    <svg
                        width="15"
                        height="8"
                        class="xl:w-auto h-auto lg:w-2.5 w-auto transition-all duration-500"
                        :style="{
                            transform: `rotate(${isCatalogShow ? 180 : 0}deg)`,
                        }"
                        viewBox="0 0 15 8"
                        fill="none"
                    >
                        <path
                            d="M0.500061 0.5L7.50006 7.5L14.5 0.5"
                            class="stroke-blue"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
                <div
                    class="flex lg:flex-row flex-col w-full lg:items-center justify-end hg:gap-x-15 2xl:gap-x-10 xl:gap-x-8 gap-x-5 gap-y-12 lg:grow"
                >
                    <div
                        class="lg:items-center flex lg:flex-row flex-col gap-x-12 gap-y-5 shrink-0 lg:grow justify-center"
                    >
                        <AppLink text="О бренде" to="/about" />
                        <AppLink text="Линейки" to="/product-line" />
                        <AppLink text="Новости" to="/news" />
                        <!--                        <AppLink text="Партнерам" to="/for-partners" />-->
                        <AppLink
                            text="#Obritитeдруга"
                            target="_blank"
                            to="https://obrititedruga.brit-rus.ru"
                            rel="noopener noreferrer"
                            :is-route="false"
                        />
                        <!--                        <AppLink text="Амбассадоры" to="/ambassadors" />-->
                    </div>
                    <div
                        class="flex lg:flex-row flex-col lg:items-center justify-end hg:gap-x-8.5 gap-x-5 gap-y-8.5"
                    >
                        <div
                            class="flex lg:flex-row flex-col lg:items-center lg:justify-end justify-center hg:gap-x-7 gap-x-3 w-full grow gap-y-5"
                        >
                            <div class="relative">
                                <router-link
                                    to="/where-to-buy"
                                    class="bg-turquoise xl:py-1.5 lg:py-1 py-1.5 2xl:px-10 xl:px-6 px-5 rounded-60 cursor-pointer shrink-0 grow-0 border border-turquoise transition-all duration-300 hover:bg-white hover:border-blue lg:mb-0 mb-12 whitespace-nowrap"
                                >
                                    Где купить
                                </router-link>
                                <HeaderSearch
                                    :is-search-bar-show="isSearchBarShow"
                                    @display-search-bar="hideSearchBarAndCatalog"
                                />
                            </div>
                            <IconSearch
                                class="cursor-pointer shrink-0 grow-0 lg:block hidden"
                                @click="displaySearchBar"
                            />
                            <!-- <button class="flex items-center gap-x-2 cursor-pointer">
                                <span class="whitespace-nowrap xl:block lg:hidden">
                                    Москва и МО
                                </span>
                            </button> -->
                            <!-- TODO: add mobile search -->
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="absolute bg-white lg:left-0 md:left-auto -left-[calc(100vw_-_295px)] lg:w-full md:w-[calc(98vw_-_295px)] w-auto lg:top-full top-0 lg:right-auto md:right-[295px] right-0 lg:bottom-auto md:bottom-0 md:h-auto h-full md:overflow-y-visible overflow-y-auto lg:rounded-b-20 py-10 px-5 md:z-10 z-30 transition-all duration-500"
            :style="{
                transform: `translateY(${isCatalogShow ? '0' : '-300vw'})`,
            }"
        >
            <div class="md:hidden block mb-9.5">
                <AppHeaderLogo class="w-[168px] h-auto mx-auto mb-6.5" />
                <button
                    class="rounded-full flex items-center justify-center py-2.5 px-5.5 bg-[#F1F1F1] gap-x-2.5 w-fit mx-auto"
                    @click="displayCatalog"
                >
                    <span>Каталог</span>
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                        <line
                            y1="-1"
                            x2="12.5508"
                            y2="-1"
                            transform="matrix(-0.698142 -0.715959 0.698143 -0.715959 10.5 8.98584)"
                            stroke="#161345"
                            stroke-width="2"
                        />
                        <line
                            y1="-1"
                            x2="12.5508"
                            y2="-1"
                            transform="matrix(-0.698143 0.715959 -0.698142 -0.715959 9.26172 0.0141602)"
                            stroke="#161345"
                            stroke-width="2"
                        />
                    </svg>
                </button>
            </div>
            <div class="lg:max-w-container mx-auto flex items-stretch xl:gap-x-[143px] gap-x-10">
                <div class="max-w-[1030px] w-full">
                    <div
                        class="grid lg:grid-cols-3 grid-cols-2 gap-y-10 gap-x-5 border-b border-blue pb-20"
                    >
                        <div class="">
                            <img
                                :src="dog"
                                alt="img"
                                width="60"
                                height="60"
                                class="lg:mb-7.5 mb-5 rounded-full object-cover object-center"
                            />
                            <p class="lg:mb-7.5 mb-5 font-gill lg:text-30 text-24 leading-none">
                                Собакам
                            </p>
                            <ul class="grid lg:gap-y-5 gap-y-2.5">
                                <li>
                                    <AppLink
                                        text="Влажный корм"
                                        to="/catalog/category/korma-vlaznye?animal_id=1"
                                    />
                                </li>
                                <li>
                                    <AppLink
                                        text="Консервы"
                                        to="/catalog?animal_id=1&sub_category_id=1"
                                    />
                                </li>
                                <li>
                                    <AppLink
                                        text="Паучи"
                                        to="/catalog?animal_id=1&sub_category_id=2"
                                    />
                                </li>
                                <!--                                <li>-->
                                <!--                                    <AppLink text="Паштеты" to="#" />-->
                                <!--                                </li>-->
                                <li>
                                    <AppLink
                                        text="Сухой корм"
                                        to="/catalog/category/korma-suxie?animal_id=1"
                                    />
                                </li>
                            </ul>
                        </div>
                        <div class="">
                            <img
                                :src="cat"
                                alt="img"
                                width="60"
                                height="60"
                                class="lg:mb-7.5 mb-5 rounded-full object-cover object-center"
                            />
                            <p class="lg:mb-7.5 mb-5 font-gill lg:text-30 text-24 leading-none">
                                Кошкам
                            </p>
                            <ul class="grid lg:gap-y-5 gap-y-2.5">
                                <li>
                                    <AppLink
                                        text="Влажный корм"
                                        to="/catalog/category/korma-vlaznye?animal_id=2"
                                    />
                                </li>
                                <li>
                                    <AppLink
                                        text="Консервы"
                                        to="/catalog?animal_id=2&sub_category_id=1"
                                    />
                                </li>
                                <li>
                                    <AppLink
                                        text="Паучи"
                                        to="/catalog?animal_id=2&sub_category_id=2"
                                    />
                                </li>
                                <!--                                <li>-->
                                <!--                                    <AppLink text="Паштеты" to="#" />-->
                                <!--                                </li>-->
                                <li>
                                    <AppLink
                                        text="Сухой корм"
                                        to="/catalog/category/korma-suxie?animal_id=2"
                                    />
                                </li>
                            </ul>
                        </div>
                        <!--                        <div class="lg:col-span-1 col-span-full">-->
                        <!--                            <img-->
                        <!--                                :src="suggest"-->
                        <!--                                alt="img"-->
                        <!--                                width="60"-->
                        <!--                                height="60"-->
                        <!--                                class="lg:mb-7.5 mb-5 rounded-full object-cover object-center"-->
                        <!--                            />-->
                        <!--                            <p class="lg:mb-7.5 mb-5 font-gill lg:text-30 text-24 leading-none">-->
                        <!--                                Подборки-->
                        <!--                            </p>-->
                        <!--                            <ul class="grid lg:gap-y-5 gap-y-2.5">-->
                        <!--                                <li>-->
                        <!--                                    <AppLink text="Для стерилизованных кошек" to="#" />-->
                        <!--                                </li>-->
                        <!--                                <li>-->
                        <!--                                    <AppLink-->
                        <!--                                        text="Для кошек с чувствительным пищеварением"-->
                        <!--                                        to="#"-->
                        <!--                                    />-->
                        <!--                                </li>-->
                        <!--                                <li>-->
                        <!--                                    <AppLink text="Для котят" to="#" />-->
                        <!--                                </li>-->
                        <!--                                <li>-->
                        <!--                                    <AppLink-->
                        <!--                                        text="Для собак мини пород с чувствительным пищеварением"-->
                        <!--                                        to="#"-->
                        <!--                                    />-->
                        <!--                                </li>-->
                        <!--                                <li>-->
                        <!--                                    <AppLink text="Для щенков мини пород" to="#" />-->
                        <!--                                </li>-->
                        <!--                            </ul>-->
                        <!--                        </div>-->
                    </div>
                    <div class="pt-10 flex items-center lg:gap-x-25 gap-x-5">
                        <router-link to="/catalog" class="flex items-center lg:gap-x-5.5 gap-x-3">
                            <span class="font-gill lg:text-24 text-20 leading-none">Каталог</span>
                            <svg width="9" height="16" viewBox="0 0 9 16" fill="none">
                                <path
                                    d="M1 15L8 8L1 1.00009"
                                    stroke="#161345"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </router-link>
                        <router-link to="/brit-care" class="flex items-center lg:gap-x-5.5 gap-x-3">
                            <span class="font-gill lg:text-24 text-20 leading-none">Brit Care</span>
                            <svg width="9" height="16" viewBox="0 0 9 16" fill="none">
                                <path
                                    d="M1 15L8 8L1 1.00009"
                                    stroke="#161345"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </router-link>
                        <router-link
                            to="/presentation"
                            class="flex items-center lg:gap-x-5.5 gap-x-3"
                        >
                            <span class="font-gill lg:text-24 text-20 leading-none">
                                Brit Premium
                            </span>
                            <svg width="9" height="16" viewBox="0 0 9 16" fill="none">
                                <path
                                    d="M1 15L8 8L1 1.00009"
                                    stroke="#161345"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </router-link>
                    </div>
                </div>
                <div class="relative h-full shrink-0 grow-0 lg:block hidden lg:basis-[330px]">
                    <img
                        :src="catalog"
                        alt="img"
                        width="330"
                        class="h-full object-center object-cover rounded-20 relative z-10 xl:w-auto w-[300px]"
                    />
                    <p
                        class="text-white top-5 font-gill text-30 text-center left-1/2 -translate-x-1/2 absolute z-20 w-full leading-none"
                    >
                        Наборы паучей
                        <br />
                        для кошек 5+1
                    </p>
                </div>
            </div>
        </div>
    </header>
</template>

<style scoped></style>
